// @ts-nocheck

import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';

// Base selectors
export const selectStudyYearCompanies = (state: RootState) => state.studyYearCompanies;
export const selectStudyYearCompaniesLoading = (state: RootState) =>
  state.studyYearCompanies.isLoading;
export const selectStudyYearCompaniesUpdating = (state: RootState) =>
  state.studyYearCompanies.isUpdating;
export const selectStudyYearCompaniesUploading = (state: RootState) =>
  state.studyYearCompanies.isUploading;
export const selectStudyYearCompaniesDownloading = (state: RootState) =>
  state.studyYearCompanies.isDownloading;
export const selectStudyYearCompaniesError = (state: RootState) => state.studyYearCompanies.error;

// Data selectors
export const selectCompanies = (state: RootState) => state.studyYearCompanies.companies;
export const selectCurrentCompany = (state: RootState) => state.studyYearCompanies.currentCompany;
export const selectCompanySegments = (state: RootState) => state.studyYearCompanies.companySegments;
export const selectCompanyContacts = (state: RootState) => state.studyYearCompanies.companyContacts;
export const selectCompanyVersions = (state: RootState) => state.studyYearCompanies.companyVersions;
export const selectCompanyHistory = (state: RootState) => state.studyYearCompanies.companyHistory;
export const selectCompaniesPagination = (state: RootState) => state.studyYearCompanies.pagination;
export const selectCompaniesFilters = (state: RootState) => state.studyYearCompanies.filters;

// Count selectors
export const selectCompaniesCount = (state: RootState) => state.studyYearCompanies.companies.length;
export const selectCompanySegmentsCount = (state: RootState) =>
  state.studyYearCompanies.companySegments.length;
export const selectCompanyContactsCount = (state: RootState) =>
  state.studyYearCompanies.companyContacts.length;
export const selectCompanyVersionsCount = (state: RootState) =>
  state.studyYearCompanies.companyVersions.length;
export const selectCompanyHistoryCount = (state: RootState) =>
  state.studyYearCompanies.companyHistory.length;

// Operation status selectors
export const selectIsAnyStudyYearCompanyOperation = createSelector(
  [
    selectStudyYearCompaniesLoading,
    selectStudyYearCompaniesUpdating,
    selectStudyYearCompaniesUploading,
    selectStudyYearCompaniesDownloading,
  ],
  (isLoading, isUpdating, isUploading, isDownloading) =>
    isLoading || isUpdating || isUploading || isDownloading
);

// Filtered companies selector
export const selectFilteredCompanies = createSelector(
  [selectCompanies, selectCompaniesFilters],
  (companies, filters) => {
    if (!filters || Object.keys(filters).length === 0) return companies;

    return companies.filter(company => {
      if (filters.year && company.year !== filters.year) return false;
      if (filters.study_id && company.study_id !== filters.study_id) return false;

      return true;
    });
  }
);

// Current company metrics selector
export const selectCurrentCompanyMetrics = createSelector([selectCurrentCompany], company => {
  if (!company) return null;

  return {
    revenue: company.revenue,
    yoyRevenue: company.yoy_revenue,
    yoyRevenuePercentage: company.yoy_revenue_percentage,
    unitsold: company.units_sold,
    yoyUnitsoldPercentage: company.yoy_units_sold_percentage,
    sellingPrice: company.selling_price,
    yoySellingPrice: company.yoy_selling_price,
    marketShare: company.market_share,
    yoyMarketShare: company.yoy_market_share,
    revenueRank: company.rank_revenue,
    rankChange: company.yoy_rank_change,
  };
});

// Current company info selector
export const selectCurrentCompanyInfo = createSelector([selectCurrentCompany], company => {
  if (!company) return null;

  return {
    id: company.study_year_company_id,
    companyId: company.company_id,
    companyName: company.company_name,
    year: company.year,
    studyYearId: company.study_year_id,
    revenueStatus: company.revenue_status,
    supplierStatus: company.supplier_status,
    priority: company.priority,
    outreachStatus: company.outreach_status,
    profileStatus: company.profile_status,
    differentiatorStatus: company.differentiator_status,
  };
});

// Company segments analysis selector
export const selectCompanySegmentsAnalysis = createSelector([selectCompanySegments], segments => {
  if (segments.length === 0) return null;

  const totalRevenue = segments.reduce((sum, segment) => sum + segment.revenue, 0);
  const totalConcentration = segments.reduce((sum, segment) => sum + segment.concentration, 0);
  const topSegment = segments.reduce((top, segment) =>
    segment.revenue > top.revenue ? segment : top
  );

  return {
    totalSegments: segments.length,
    totalRevenue,
    totalConcentration,
    averageRevenue: totalRevenue / segments.length,
    averageConcentration: totalConcentration / segments.length,
    topSegment,
    segmentsWithGrowth: segments.filter(s => s.yoy_revenue_percentage > 0).length,
  };
});

// Companies performance analysis selector
export const selectCompaniesAnalysis = createSelector([selectCompanies], companies => {
  if (companies.length === 0) return null;

  const totalRevenue = companies.reduce((sum, company) => sum + company.revenue, 0);
  const companiesWithGrowth = companies.filter(c => c.yoy_revenue_percentage > 0);
  const topPerformer = companies.reduce((top, company) =>
    company.revenue > top.revenue ? company : top
  );

  const averageGrowth =
    companies.reduce((sum, company) => sum + company.yoy_revenue_percentage, 0) / companies.length;

  return {
    totalCompanies: companies.length,
    totalRevenue,
    averageRevenue: totalRevenue / companies.length,
    averageGrowth,
    companiesWithGrowth: companiesWithGrowth.length,
    growthRate: (companiesWithGrowth.length / companies.length) * 100,
    topPerformer,
  };
});

// Companies by status selector
export const selectCompaniesByStatus = createSelector([selectCompanies], companies => {
  const byRevenueStatus = companies.reduce(
    (acc, company) => {
      const status = company.revenue_status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const byOutreachStatus = companies.reduce(
    (acc, company) => {
      const status = company.outreach_status || 'not_started';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const byPriority = companies.reduce(
    (acc, company) => {
      const priority = company.priority || 'medium';
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return {
    byRevenueStatus,
    byOutreachStatus,
    byPriority,
  };
});

// Company contacts by department selector
export const selectContactsByDepartment = createSelector([selectCompanyContacts], contacts => {
  if (contacts.length === 0) return null;

  // Assuming contacts have a department field (you may need to adjust based on PersonSchema)
  const byDepartment = contacts.reduce(
    (acc, contact) => {
      const dept = (contact as any).department || 'Other';
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(contact);
      return acc;
    },
    {} as Record<string, typeof contacts>
  );

  return {
    byDepartment,
    totalContacts: contacts.length,
    departments: Object.keys(byDepartment),
  };
});
